import type * as React from "react";
import { forwardRef } from "react";
import type { LucideIcon } from "lucide-react";
import { Plus } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Tonal — an aside frame. A quieter register than Card.
 *
 * Tone-only framing: one step darker than canvas, borderless, shadowless,
 * straight corners. Use to group content that reads as a section rather
 * than a discrete object (situation briefs, suggested-fix asides,
 * empty-state framings).
 *
 * Padding scale:
 *   - sm: py-4 px-5   (inline asides)
 *   - md: py-6 px-7   (default — narrative asides)
 *   - lg: py-8 px-9   (hero editorial blocks)
 */

const paddingStyles = {
  sm: "px-4 py-3 sm:px-5 sm:py-4",
  md: "px-4 py-4 sm:px-7 sm:py-6",
  lg: "px-5 py-6 sm:px-9 sm:py-8",
} as const;

interface TonalProps extends React.ComponentProps<"div"> {
  padding?: "sm" | "md" | "lg";
}

export function Tonal({ className, padding = "md", ...props }: TonalProps) {
  return (
    <div
      data-slot="tonal"
      className={cn(
        "bg-surface-tonal border border-border/60 rounded-md",
        paddingStyles[padding],
        className,
      )}
      {...props}
    />
  );
}

/**
 * TonalInvitation — a tonal-register CTA that doubles as an empty-state prompt.
 *
 * A clickable surface sharing Tonal's recessed background, with an icon
 * circle on the left that fills with the primary color on hover. Use in
 * place of dashed-border "add …" placeholders so empty-state affordances
 * live in the same editorial vocabulary as the Situation brief.
 *
 * Shape:
 *   - icon circle (default: Plus) with primary fill on hover
 *   - semibold sans title (larger on size="lg")
 *   - muted sans subtitle
 *
 * Size scale:
 *   - md: px-7 py-6, 36px icon  (default — inline CTA)
 *   - lg: px-9 py-8, 44px icon  (hero empty-state)
 */

interface TonalInvitationProps
  extends Omit<React.ComponentProps<"button">, "title"> {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: LucideIcon;
  size?: "md" | "lg";
  disabled?: boolean;
}

export const TonalInvitation = forwardRef<
  HTMLButtonElement,
  TonalInvitationProps
>(function TonalInvitation(
  {
    className,
    title,
    subtitle,
    icon: Icon = Plus,
    size = "md",
    disabled,
    type = "button",
    ...props
  },
  ref,
) {
  const isLg = size === "lg";
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      data-slot="tonal-invitation"
      className={cn(
        "group flex w-full items-center gap-5 bg-surface-tonal text-left transition-colors",
        isLg ? "px-9 py-8" : "px-7 py-6",
        disabled
          ? "cursor-not-allowed opacity-60"
          : "hover:bg-foreground/[0.04]",
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full transition-colors",
          isLg ? "h-11 w-11" : "h-9 w-9",
          disabled
            ? "bg-muted text-muted-foreground"
            : "bg-primary/5 text-primary-ink group-hover:bg-primary group-hover:text-primary-foreground",
        )}
      >
        <Icon
          className={cn(isLg ? "h-5 w-5" : "h-4 w-4")}
          strokeWidth={2.25}
          aria-hidden="true"
        />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block font-semibold",
            isLg ? "text-md" : "text-base",
            disabled ? "text-muted-foreground" : "text-foreground",
          )}
        >
          {title}
        </span>
        {subtitle && (
          <span
            className={cn(
              "mt-0.5 block text-muted-foreground",
              isLg ? "text-base" : "text-sm",
            )}
          >
            {subtitle}
          </span>
        )}
      </span>
    </button>
  );
});
